"use client";

import { BookInterface } from "../../books/types/book";
import { formatSaleYear, formatPageCount } from "../../books/utils/formatters";
import { useEffect, useState, useCallback, useRef } from "react";
import { createClient } from '@/utils/supabase/client' // Create a client-side version
import styles from "../styles.module.css"
import { track } from "@vercel/analytics/react";
import { formatRating } from "../../books/utils/formatters";
import { calculatePercentageRating, BookRatings } from "../../books/utils/ratings";
import { fetchBookRating } from "@/app/actions/bookActions";

interface BookHeaderProps {
    book: BookInterface;
}

const BookHeader = ({ book }: BookHeaderProps) => {
  const [rating, setRating] = useState<BookRatings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef<boolean>(false);

  const supabase = createClient();

  // Memoize the loadBookRating function
  const loadBookRating = useCallback(async () => {
    if (!book.work_urn || loadingRef.current) return;
    
    loadingRef.current = true;
    setLoading(true);

    try {
      const result = await fetchBookRating(book.work_urn, window.location.href);
      setRating(result.rating);
      if (result.error) {
        setError(result.error);
      }
    } catch (err) {
      console.error("Error loading book rating:", err);
      setError("Failed to load book rating");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [book.work_urn]);

  // Start loading as soon as component mounts
  useEffect(() => {
    loadBookRating();
  }, [loadBookRating]);

  // Calculate scores only when rating is available
  const positivePercentage = rating ? calculatePercentageRating(rating) : 0;

  return(
    <div>
      <img 
        src={`https://zilu-cover-images.storage.googleapis.com/${book.isbn_13}.jpg`}
        alt={book.title}
        className="book-detail-cover mb-6"
      />
      <div className="header">
        <h1 className="title">{book.title}</h1>
        <p className="author">{book.author}</p>
        <div className="meta">
          <span>{formatSaleYear(book.onsale)} · {formatPageCount({ pages: book.pages })} </span>
          <div className="min-h-[24px]">
            {loading ? (
              <div>Loading ratings...</div>
            ) : error ? (
              <div>{error}</div>
            ) : rating && positivePercentage !== 0 ? (
              <div>
                <span className="flex ">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.like_symbol}>
                    <path d="M14.3373 8.0851C14.2773 7.8679 14.1632 7.6687 14.0191 7.4935C13.8691 7.3243 13.6829 7.1857 13.4788 7.0891C13.2747 6.9925 13.0286 6.9565 12.8305 6.9505H10.5216V5.6407C10.5216 5.0977 10.3114 4.5841 9.92721 4.1977C9.54299 3.8113 9.0327 3.6001 8.49239 3.6001C8.25226 3.6001 8.04214 3.7387 7.94608 3.9565L6.19309 7.9165H5.15449C4.74026 7.9165 4.35004 8.0797 4.05587 8.3755C3.7617 8.6713 3.59961 9.0637 3.59961 9.4801V12.8365C3.59961 13.2529 3.7617 13.6453 4.05587 13.9411C4.35004 14.2369 4.74026 14.4001 5.15449 14.4001H12.1881C12.5603 14.4001 12.9145 14.2675 13.1967 14.0257C13.4848 13.7785 13.6709 13.4401 13.725 13.0717L14.3853 8.7553C14.4154 8.5321 14.3974 8.2963 14.3373 8.0851ZM5.99017 13.1917H5.1617C5.06744 13.1923 4.97679 13.1557 4.90956 13.0891C4.84352 13.0219 4.8063 12.9301 4.8075 12.8353V9.4789C4.8075 9.3823 4.84352 9.2917 4.90956 9.2251C4.97679 9.1585 5.06744 9.1219 5.1617 9.1225H5.99017V13.1917Z" fill="#1E1C1C"/>
                  </svg>
                  <p className={styles.percentage_liked}> {positivePercentage}% liked this book </p>
                  <p className={styles.total_ratings}> {formatRating(rating.total_ratings)} </p>
                </span>
              </div>
            ) : (
              <div></div>
              // need a fall back where there are no 4 or 5 star reviews or only an average review
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookHeader;